import { useTranslation } from "react-i18next"

import { Textarea } from "@/components/ui/textarea"

import { cellText, collectEditableCells, setCellValue } from "./composite-editor-utils"

/**
 * Per-cell editor for a composite section suggestion.
 *
 * Composite suggestions previously had no Edit path at all: the button was
 * hidden and the section could only be accepted or rejected wholesale (#2471).
 *
 * A cell the user changes is marked `origin: "manual"` right here, so the
 * backend's composite accept — which preserves each incoming origin when the
 * accept is not a whole-field retype — records exactly which cells a human
 * authored and leaves the rest attributed to the AI.
 */
export function CompositeEditor({
  section,
  onChange,
  testIdPrefix,
}: {
  section: unknown
  onChange: (next: unknown) => void
  testIdPrefix?: string
}) {
  const { t } = useTranslation()
  const cells = collectEditableCells(section)

  if (cells.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">{t("segmentAnalysis.review.noEditableCells")}</p>
    )
  }

  return (
    <div
      className="grid gap-3"
      data-testid={testIdPrefix ? `${testIdPrefix}-composite-editor` : undefined}
    >
      {cells.map((cell) => {
        const key = cell.path.join(".")
        const isList = Array.isArray(cell.field.value)
        return (
          <label key={key} className="grid gap-1">
            <span className="text-muted-foreground text-[11px] font-semibold">{cell.label}</span>
            <Textarea
              className="min-h-16 text-sm"
              data-testid={`${testIdPrefix}-composite-cell-${key}`}
              value={cellText(cell.field)}
              onChange={(event) => {
                const raw = event.target.value
                const value = isList
                  ? raw
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean)
                  : raw
                onChange(
                  setCellValue(section, cell.path, {
                    ...cell.field,
                    value,
                    // Mark only the cells the user actually touched.
                    origin: "manual",
                    status: value.length > 0 ? "grounded" : cell.field.status,
                  })
                )
              }}
            />
          </label>
        )
      })}
    </div>
  )
}
